import { useState, useEffect, useMemo, useRef } from "react";
import "../../styles/website/Home.css";
import "../../styles/website/ProductsSearch.css";
import Layout from "../website/component/Layout.jsx";
import {
  fetchProvinces,
  fetchCities,
  fetchStoreTypes,
} from "../../api/storeRequest";
import { getProducts } from "../../api/shopsApi.js";

/* =========================================================
   تنظیمات
   ========================================================= */

// تعداد محصول در هر صفحه (باید با page_size بک‌اند هماهنگ باشه؛ max_page_size بک‌اند = 100)
const PAGE_SIZE = 12;
// حداکثر محصول هر دسته در نمای اولیه (بدون فیلتر)
const MAX_PER_CATEGORY = 4;
const ALL = "all"; // مقدار «همه دسته‌بندی‌ها» در سلکت
const FALLBACK_CATEGORY = "سایر";

// استایل سکشن‌ها به‌صورت چرخشی (وقتی «همه» انتخابه)
// grid: گرید ساده | scroll: ردیف افقی روی زمینه‌ی کرمی
// feature: پنل رنگی با هدر روی گرادیان | bento: کارت اول بزرگ
// (محدودیت تعداد هر سکشن نداریم؛ تعداد رو صفحه‌بندی بک‌اند کنترل می‌کنه)
const VARIANTS = ["grid", "scroll", "feature", "bento"];

const EMPTY_FILTERS = {
  q: "",
  category: ALL,
  province: "",
  city: "",
  minPrice: "",
  maxPrice: "",
  page: 1,
};

/* =========================================================
   ابزارها
   ========================================================= */

// خروجی لیستی بک‌اند: یا آرایه‌ی ساده یا صفحه‌بندی‌شده ({ results: [...] })
const toList = (data) => (Array.isArray(data) ? data : data?.results || []);

// قیمت از بک‌اند به شکل رشته میاد ("1250000")
const formatPrice = (price) => {
  const n = Number(price);
  return Number.isFinite(n) && price !== null && price !== ""
    ? n.toLocaleString("fa-IR") + " تومان"
    : "";
};

// تبدیل ارقام فارسی/عربی به انگلیسی و نگه داشتن فقط رقم‌ها
const toDigits = (value) =>
  String(value)
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/\D/g, "");

// نمایش عدد داخل اینپوت با جداکننده‌ی هزارگان
const formatPriceInput = (value) =>
  value === "" ? "" : Number(value).toLocaleString("fa-IR");

// دسته‌بندی فروشگاهی محصول
// ⚠️ بک‌اند باید store_category_name (یا store_category با title/name) برگردونه
const getStoreCategory = (product) => {
  const c = product.category || product.store?.category;
  const name =
    c?.title ||
    c?.name ||
    product.store_category_name ||
    (typeof c === "string" ? c : "");
  return (name || "").trim() || FALLBACK_CATEGORY;
};

// گروه‌بندی با حفظ ترتیب اولین ظهور؛ «سایر» همیشه آخر
const groupByStoreCategory = (list) => {
  const map = new Map();
  for (const p of list) {
    const key = getStoreCategory(p);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  const groups = [...map.entries()].map(([name, items]) => ({ name, items }));
  return groups.sort((a, b) =>
    a.name === FALLBACK_CATEGORY ? 1 : b.name === FALLBACK_CATEGORY ? -1 : 0,
  );
};

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </svg>
  );
}

function Pagination({ page, totalPages, onChange, disabled }) {
  if (totalPages <= 1) return null;

  // صفحه‌ی اول، آخر و دو صفحه دور و بر صفحه‌ی فعلی؛ بقیه «…»
  const items = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) items.push(i);
    else if (items[items.length - 1] !== "…") items.push("…");
  }

  return (
    <nav className="ps-pagination" aria-label="صفحه‌بندی">
      <button
        type="button"
        className="ps-page-btn"
        onClick={() => onChange(page - 1)}
        disabled={disabled || page <= 1}
      >
        قبلی
      </button>

      {items.map((it, i) =>
        it === "…" ? (
          <span key={`gap-${i}`} className="ps-page-gap" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            className={`ps-page-btn${it === page ? " is-active" : ""}`}
            onClick={() => onChange(it)}
            disabled={disabled}
            aria-current={it === page ? "page" : undefined}
          >
            {it.toLocaleString("fa-IR")}
          </button>
        ),
      )}

      <button
        type="button"
        className="ps-page-btn"
        onClick={() => onChange(page + 1)}
        disabled={disabled || page >= totalPages}
      >
        بعدی
      </button>
    </nav>
  );
}

function ProductCard({ product }) {
  return (
    <li>
      <a
        className="tile product-card"
        href={`${product.store_slug}/product/${product.product_slug}`}
      >
        <div className="product-img">
          {product.cover_image && (
            <img src={product.cover_image} alt="" loading="lazy" />
          )}
        </div>
        <h3 title={product.name}>{product.name}</h3>
        {product.store_name && (
          <span className="product-store">{product.store_name}</span>
        )}
        <span className="product-price">{formatPrice(product.price)}</span>
      </a>
    </li>
  );
}

/* =========================================================
   صفحه
   ========================================================= */

export default function ProductsSearch() {
  const params = new URLSearchParams(window.location.search);
  const initial = {
    q: (params.get("q") || "").trim(),
    category: params.get("category") || ALL,
    province: params.get("province") || "",
    city: params.get("city") || "",
    minPrice: toDigits(params.get("min") || ""),
    maxPrice: toDigits(params.get("max") || ""),
    page: Math.max(1, parseInt(params.get("page"), 10) || 1),
  };
  // صفحه‌بندی فقط وقتی فیلتر/«مشاهده همه» فعاله؛ بدون فیلتر همیشه صفحه‌ی ۱
  if (
    !initial.q &&
    initial.category === ALL &&
    !initial.province &&
    !initial.city &&
    initial.minPrice === "" &&
    initial.maxPrice === ""
  ) {
    initial.page = 1;
  }

  // مقدارهای در حال ویرایش (هنوز به بکند نرفتن)
  const [query, setQuery] = useState(initial.q);
  const [selectedCategory, setSelectedCategory] = useState(initial.category);
  const [selectedProvince, setSelectedProvince] = useState(initial.province);
  const [selectedCity, setSelectedCity] = useState(initial.city);
  const [minPrice, setMinPrice] = useState(initial.minPrice);
  const [maxPrice, setMaxPrice] = useState(initial.maxPrice);

  // مقدارهای اعمال‌شده؛ فقط با کلیک روی دکمه‌ی جستجو عوض می‌شن
  const [applied, setApplied] = useState(initial);

  const [categories, setCategories] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // پنل کشویی فیلترها
  const [filterOpen, setFilterOpen] = useState(false);
  const formRef = useRef(null);

  const groups = useMemo(() => groupByStoreCategory(products), [products]);
  // اگه بک‌اند هنوز دسته‌بندی محصول رو نمی‌فرسته، همه تو «سایر» می‌افتن؛ اون موقع گرید ساده نشون می‌دیم
  const flat = groups.length === 1 && groups[0].name === FALLBACK_CATEGORY;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const isFiltered = applied.category !== ALL;
  // تعداد فیلترهای اعمال‌شده‌ی پنل (برای بج روی آیکون)
  const activeCount =
    (applied.province ? 1 : 0) +
    (applied.city ? 1 : 0) +
    (applied.minPrice !== "" || applied.maxPrice !== "" ? 1 : 0);
  const hasAnyFilter =
    !!applied.q ||
    isFiltered ||
    !!applied.province ||
    !!applied.city ||
    applied.minPrice !== "" ||
    applied.maxPrice !== "";

  // دریافت دسته‌بندی‌های فروشگاه‌ها و استان‌ها (فقط یک بار)
  useEffect(() => {
    let cancelled = false;
    fetchStoreTypes()
      .then((res) => {
        if (cancelled) return;
        setCategories(toList(res.data));
      })
      .catch(() => !cancelled && setCategories([]));
    fetchProvinces()
      .then((res) => {
        if (cancelled) return;
        setProvinces(toList(res.data));
      })
      .catch(() => !cancelled && setProvinces([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // دریافت شهرهای استان انتخاب‌شده
  useEffect(() => {
    if (!selectedProvince) {
      setCities([]);
      return;
    }

    let cancelled = false;
    fetchCities(selectedProvince)
      .then((res) => {
        if (cancelled) return;
        setCities(toList(res.data));
      })
      .catch(() => !cancelled && setCities([]));
    return () => {
      cancelled = true;
    };
  }, [selectedProvince]);
  const isFirstRequest = useRef(true);
  // دریافت محصولات؛ فقط وقتی «applied» عوض بشه (یعنی کلیک روی دکمه)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        // ترتیب پارامترها باید دقیقاً مثل تعریف getProducts باشه:
        // (search, city, province, store_category, min_price, max_price, page, page_size)
        const shouldSendPageSize = !isFirstRequest.current;
        isFirstRequest.current = false;

        const res = await getProducts(
          applied.q || undefined,
          applied.city || undefined,
          applied.province || undefined,
          applied.category !== ALL ? applied.category : undefined,
          applied.minPrice || undefined,
          applied.maxPrice || undefined,
          applied.page,
          hasAnyFilter ? PAGE_SIZE : undefined,
        );
        if (cancelled) return;
        const data = res.data;
        const list = toList(data);
        setProducts(list);
        setCount(
          Array.isArray(data) ? list.length : (data.count ?? list.length),
        );
      } catch (err) {
        if (cancelled) return;
        // صفحه‌ی خارج از محدوده (مثلاً ?page=99) → DRF خطای 404 میده؛ برمی‌گردیم صفحه‌ی ۱
        if (err?.response?.status === 404 && applied.page > 1) {
          setApplied((a) => ({ ...a, page: 1 }));
          return;
        }
        setProducts([]);
        setCount(0);
        setError("دریافت محصولات انجام نشد. دوباره تلاش کن.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [applied]);

  // همگام‌سازی با آدرس صفحه (قابل اشتراک‌گذاری)
  useEffect(() => {
    const p = new URLSearchParams();
    if (applied.q) p.set("q", applied.q);
    if (applied.category !== ALL) p.set("category", applied.category);
    if (applied.province) p.set("province", applied.province);
    if (applied.city) p.set("city", applied.city);
    if (applied.minPrice !== "") p.set("min", applied.minPrice);
    if (applied.maxPrice !== "") p.set("max", applied.maxPrice);
    if (applied.page > 1) p.set("page", String(applied.page));
    const s = p.toString();
    window.history.replaceState(
      null,
      "",
      s ? `?${s}` : window.location.pathname,
    );
  }, [applied]);

  // بستن پنل با کلیک بیرون یا Escape
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (e) => {
      if (formRef.current && !formRef.current.contains(e.target))
        setFilterOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setFilterOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [filterOpen]);

  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
    setSelectedCity(""); // شهر قبلی مال استان دیگه‌ست
  };

  const submitSearch = (e) => {
    e?.preventDefault();
    setFilterOpen(false);

    // اگه کاربر بازه رو برعکس زده، جابه‌جاشون می‌کنیم
    let lo = minPrice;
    let hi = maxPrice;
    if (lo !== "" && hi !== "" && Number(lo) > Number(hi)) [lo, hi] = [hi, lo];
    setMinPrice(lo);
    setMaxPrice(hi);

    setApplied({
      q: query.trim(),
      category: selectedCategory,
      province: selectedProvince,
      city: selectedCity,
      minPrice: lo,
      maxPrice: hi,
      page: 1, // جستجوی جدید همیشه از صفحه‌ی اول
    });
  };

  const resetSearch = () => {
    setQuery("");
    setSelectedCategory(ALL);
    setSelectedProvince("");
    setSelectedCity("");
    setMinPrice("");
    setMaxPrice("");
    setApplied(EMPTY_FILTERS);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === applied.page) return;
    setApplied({ ...applied, page });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // «مشاهده همه» در هر سکشن: فقط همون دسته رو فیلتر می‌کنه (از صفحه‌ی ۱)
  const showAllOf = (cat) => {
    if (!cat) return;
    const id = String(cat.type_id);
    setSelectedCategory(id);
    setApplied({ ...applied, category: id, page: 1 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const findCategoryByName = (name) => categories.find((c) => c.title === name);

  return (
    <Layout>
      {/* ---------- Hero ---------- */}
      <section className="ps-hero">
        <div className="wrap ps-hero-inner">
          <h1>جستجو در همه محصولات</h1>
          <p className="ps-hero-sub">
            اسم محصول رو بنویس یا با دسته‌بندی، موقعیت و بازه‌ی قیمت فیلتر کن.
          </p>
        </div>
      </section>

      {/* ---------- جستجو + فیلتر + نتایج ---------- */}
      <section className="ps-body">
        <div className="wrap">
          <form className="ps-searchbar" onSubmit={submitSearch} ref={formRef}>
            {/* فیلتر دسته‌بندی فروشگاه‌ها (سلکت‌باکس) */}
            <div className="ps-select">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                aria-label="دسته‌بندی فروشگاه"
              >
                <option value={ALL}>همه دسته‌بندی‌ها</option>
                {categories.map((c) => (
                  <option key={c.type_id} value={String(c.type_id)}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="ps-search">
              <SearchIcon />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="اسم محصول رو بنویس…"
                aria-label="جستجوی محصول"
              />
            </div>

            {/* دکمه‌ی فیلتر؛ پنل کشویی رو باز می‌کنه */}
            <button
              type="button"
              className={`ps-filter-btn${filterOpen ? " is-open" : ""}${
                activeCount ? " has-active" : ""
              }`}
              onClick={() => setFilterOpen((o) => !o)}
              aria-expanded={filterOpen}
              aria-controls="ps-filter-panel"
              aria-label="فیلترها"
            >
              <FilterIcon />
              {activeCount > 0 && (
                <span className="ps-filter-badge">
                  {activeCount.toLocaleString("fa-IR")}
                </span>
              )}
            </button>

            <button type="submit" className="ps-submit" disabled={loading}>
              جستجو
            </button>

            {/* ---------- پنل کشویی فیلترها ---------- */}
            {filterOpen && (
              <div className="ps-filter-panel" id="ps-filter-panel">
                <div className="ps-filter-grid">
                  <div className="ps-field">
                    <span className="ps-field-label">استان</span>
                    <div className="ps-select">
                      <select
                        value={selectedProvince}
                        onChange={(e) => handleProvinceChange(e.target.value)}
                        aria-label="استان"
                      >
                        <option value="">همه استان‌ها</option>
                        {provinces.map((p) => (
                          <option
                            key={p.province_id}
                            value={String(p.province_id)}
                          >
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="ps-field">
                    <span className="ps-field-label">شهر</span>
                    <div className="ps-select">
                      <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        disabled={!selectedProvince}
                        aria-label="شهر"
                      >
                        <option value="">
                          {selectedProvince
                            ? "همه شهرها"
                            : "اول استان رو انتخاب کن"}
                        </option>
                        {cities.map((c) => (
                          <option key={c.city_id} value={String(c.city_id)}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="ps-field ps-field-wide">
                    <span className="ps-field-label">بازه قیمت (تومان)</span>
                    <div className="ps-price-row">
                      <label className="ps-search ps-price">
                        <span className="ps-price-label">از</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatPriceInput(minPrice)}
                          onChange={(e) =>
                            setMinPrice(toDigits(e.target.value))
                          }
                          placeholder="کمترین"
                          aria-label="کمترین قیمت (تومان)"
                        />
                      </label>
                      <label className="ps-search ps-price">
                        <span className="ps-price-label">تا</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatPriceInput(maxPrice)}
                          onChange={(e) =>
                            setMaxPrice(toDigits(e.target.value))
                          }
                          placeholder="بیشترین"
                          aria-label="بیشترین قیمت (تومان)"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>

          {hasAnyFilter && (
            <div className="ps-clear-row">
              <button type="button" className="ps-more" onClick={resetSearch}>
                پاک کردن همه فیلترها
              </button>
            </div>
          )}

          {loading && <p className="ps-state">در حال بارگذاری…</p>}

          {!loading && error && <p className="ps-state ps-error">{error}</p>}

          {!loading && !error && (
            <>
              <p className="ps-count">{count.toLocaleString("fa-IR")} محصول</p>

              {products.length === 0 ? (
                <div className="tile ps-empty">
                  <p>محصولی با این شرایط پیدا نشد.</p>
                  {hasAnyFilter && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={resetSearch}
                    >
                      پاک کردن جستجو
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="ps-groups">
                    {groups.map((group, idx) => {
                      // دسته‌ی فیلترشده یا حالت بدون دسته‌بندی → گرید ساده
                      const variant =
                        isFiltered || flat
                          ? "grid"
                          : VARIANTS[idx % VARIANTS.length];
                      const cat = findCategoryByName(group.name);
                      const canShowAll =
                        !isFiltered && groups.length > 1 && cat;
                      // نمای اولیه: فقط چند محصول اول هر دسته؛ با فیلتر یا «مشاهده همه» همه‌ی نتایج صفحه
                      const visibleItems = hasAnyFilter
                        ? group.items
                        : group.items.slice(0, MAX_PER_CATEGORY);

                      return (
                        <section
                          key={group.name}
                          className={`ps-group ps-v-${variant}`}
                        >
                          {!flat && (
                            <div className="ps-group-head">
                              <h2 className="ps-group-title">{group.name}</h2>
                              {canShowAll && (
                                <button
                                  type="button"
                                  className="ps-more"
                                  onClick={() => showAllOf(cat)}
                                >
                                  مشاهده همه
                                </button>
                              )}
                            </div>
                          )}
                          <ul className="ps-grid">
                            {visibleItems.map((product) => (
                              <ProductCard key={product.id} product={product} />
                            ))}
                          </ul>
                        </section>
                      );
                    })}
                  </div>
                  {/* «مشاهده همه» دسته هم category رو ست می‌کنه، پس hasAnyFilter میشه */}
                  {hasAnyFilter && (
                    <Pagination
                      page={applied.page}
                      totalPages={totalPages}
                      onChange={goToPage}
                      disabled={loading}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
